import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Paper,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  CardActions
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Science as ScienceIcon,
  ArrowForward as ArrowForwardIcon,
  Verified as VerifiedIcon
} from '@mui/icons-material';
import { setWaterProfile, WorkflowStage } from '../../store/slices/workflowSlice';

interface WaterProfile {
  id: string;
  name: string;
  description?: string;
  ph: number;
  ec: number;
  elements: {
    elementSymbol: string;
    concentration: number;
  }[];
}

interface AnalysisResult {
  overallScore: number;
  phStatus: 'optimal' | 'acceptable' | 'warning' | 'critical';
  ecStatus: 'optimal' | 'acceptable' | 'warning' | 'critical';
  elementAnalysis: {
    symbol: string;
    concentration: number;
    status: 'optimal' | 'acceptable' | 'warning' | 'critical';
    recommendation: string;
  }[];
  recommendations: string[];
  compatibility: 'excellent' | 'good' | 'fair' | 'poor';
}

const WaterProfileAnalysis: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [waterProfiles, setWaterProfiles] = useState<WaterProfile[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Optimal ranges for hydroponic water quality
  const optimalRanges = {
    ph: { min: 5.5, max: 6.5, optimal: 6.0 },
    ec: { min: 0.8, max: 2.0, optimal: 1.2 },
    elements: {
      Ca: { min: 150, max: 200, optimal: 175, unit: 'ppm' },
      Mg: { min: 50, max: 75, optimal: 60, unit: 'ppm' },
      K: { min: 200, max: 300, optimal: 250, unit: 'ppm' },
      Na: { min: 0, max: 50, optimal: 0, unit: 'ppm' },
      Cl: { min: 0, max: 75, optimal: 0, unit: 'ppm' },
      S: { min: 60, max: 120, optimal: 90, unit: 'ppm' }
    }
  };

  useEffect(() => {
    fetchWaterProfiles();
  }, []);

  const fetchWaterProfiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/water-profiles?includeElements=true');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const profiles = await response.json();
      setWaterProfiles(profiles);
      
      // Auto-select first profile if available
      if (profiles.length > 0 && !selectedProfileId) {
        setSelectedProfileId(profiles[0].id);
        analyzeProfile(profiles[0]);
      }
    } catch (error) {
      console.error('Error fetching water profiles:', error);
      setError('Failed to load water profiles from database');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (profileId: string) => {
    setSelectedProfileId(profileId);
    const profile = waterProfiles.find(p => p.id === profileId);
    if (profile) {
      analyzeProfile(profile);
    }
  };

  const analyzeProfile = async (profile: WaterProfile) => {
    setAnalyzing(true);
    
    try {
      // Simulate analysis delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const analysis = performAnalysis(profile);
      setAnalysisResult(analysis);
    } catch (error) {
      console.error('Error analyzing profile:', error);
      setError('Failed to analyze water profile');
    } finally {
      setAnalyzing(false);
    }
  };

  const performAnalysis = (profile: WaterProfile): AnalysisResult => {
    let totalScore = 0;
    let scoreCount = 0;
    
    // Analyze pH
    const phStatus = getParameterStatus(profile.ph, optimalRanges.ph);
    totalScore += getStatusScore(phStatus);
    scoreCount++;
    
    // Analyze EC
    const ecStatus = getParameterStatus(profile.ec, optimalRanges.ec);
    totalScore += getStatusScore(ecStatus);
    scoreCount++;
    
    // Analyze elements
    const elementAnalysis = Object.entries(optimalRanges.elements).map(([symbol, range]) => {
      const element = profile.elements.find(e => e.elementSymbol === symbol);
      const concentration = element?.concentration || 0;
      const status = getParameterStatus(concentration, range);
      
      totalScore += getStatusScore(status);
      scoreCount++;
      
      return {
        symbol,
        concentration,
        status,
        recommendation: getElementRecommendation(symbol, concentration, range, status)
      };
    });
    
    const overallScore = Math.round((totalScore / scoreCount) * 100);
    
    // Generate recommendations
    const recommendations = generateRecommendations(profile, phStatus, ecStatus, elementAnalysis);
    
    // Determine compatibility
    const compatibility = getCompatibilityRating(overallScore);
    
    return {
      overallScore,
      phStatus,
      ecStatus,
      elementAnalysis,
      recommendations,
      compatibility
    };
  };

  const getParameterStatus = (value: number, range: any): 'optimal' | 'acceptable' | 'warning' | 'critical' => {
    const optimalRange = range.optimal ? 0.1 : 0.2; // 10% or 20% tolerance
    const acceptableRange = 0.3; // 30% tolerance
    
    if (range.optimal) {
      const tolerance = range.optimal * optimalRange;
      if (Math.abs(value - range.optimal) <= tolerance) return 'optimal';
    }
    
    if (value >= range.min && value <= range.max) {
      const midRange = (range.max - range.min) * acceptableRange;
      if (value >= range.min + midRange && value <= range.max - midRange) {
        return 'acceptable';
      }
      return 'warning';
    }
    
    return 'critical';
  };

  const getStatusScore = (status: string): number => {
    switch (status) {
      case 'optimal': return 1.0;
      case 'acceptable': return 0.8;
      case 'warning': return 0.6;
      case 'critical': return 0.3;
      default: return 0.5;
    }
  };

  const getElementRecommendation = (symbol: string, concentration: number, range: any, status: string): string => {
    if (status === 'optimal') return 'Concentration is optimal for hydroponic cultivation';
    if (status === 'acceptable') return 'Concentration is within acceptable range';
    
    if (concentration < range.min) {
      return `Increase ${symbol} concentration to at least ${range.min} ${range.unit}`;
    } else if (concentration > range.max) {
      return `Reduce ${symbol} concentration to below ${range.max} ${range.unit}`;
    }
    
    return 'Monitor concentration levels';
  };

  const generateRecommendations = (profile: WaterProfile, phStatus: string, ecStatus: string, elementAnalysis: any[]): string[] => {
    const recommendations = [];
    
    if (phStatus !== 'optimal') {
      if (profile.ph < optimalRanges.ph.min) {
        recommendations.push('Consider pH adjustment using potassium hydroxide (KOH) to raise pH');
      } else if (profile.ph > optimalRanges.ph.max) {
        recommendations.push('Consider pH adjustment using phosphoric acid to lower pH');
      }
    }
    
    if (ecStatus !== 'optimal') {
      if (profile.ec < optimalRanges.ec.min) {
        recommendations.push('Low EC indicates low dissolved mineral content - may need nutrient supplementation');
      } else if (profile.ec > optimalRanges.ec.max) {
        recommendations.push('High EC indicates high dissolved mineral content - consider water dilution or filtration');
      }
    }
    
    const criticalElements = elementAnalysis.filter(e => e.status === 'critical');
    if (criticalElements.length > 0) {
      recommendations.push(`Critical attention needed for: ${criticalElements.map(e => e.symbol).join(', ')}`);
    }
    
    const highSodium = elementAnalysis.find(e => e.symbol === 'Na' && e.concentration > 30);
    if (highSodium) {
      recommendations.push('High sodium levels may cause nutrient lockout - consider reverse osmosis filtration');
    }
    
    const highChloride = elementAnalysis.find(e => e.symbol === 'Cl' && e.concentration > 50);
    if (highChloride) {
      recommendations.push('High chloride levels may be toxic to plants - consider alternative water source');
    }
    
    if (recommendations.length === 0) {
      recommendations.push('Water quality is excellent for hydroponic cultivation');
    }
    
    return recommendations;
  };

  const getCompatibilityRating = (score: number): 'excellent' | 'good' | 'fair' | 'poor' => {
    if (score >= 90) return 'excellent';
    if (score >= 75) return 'good';
    if (score >= 60) return 'fair';
    return 'poor';
  };

  const handleConfirmWaterProfile = async () => {
    if (!selectedProfileId || !analysisResult) {
      return;
    }

    setConfirming(true);

    try {
      const selectedProfile = waterProfiles.find(p => p.id === selectedProfileId);
      if (!selectedProfile) {
        throw new Error('Selected water profile not found');
      }

      // Convert water profile to workflow format
      const workflowWaterProfile = {
        id: selectedProfile.id,
        name: selectedProfile.name,
        ph: selectedProfile.ph,
        ec: selectedProfile.ec,
        elements: selectedProfile.elements.map(element => ({
          symbol: element.elementSymbol,
          concentration: element.concentration,
          unit: 'ppm' // Default unit
        })),
        analysisResults: {
          qualityScore: analysisResult.overallScore,
          compatibility: analysisResult.compatibility,
          recommendations: analysisResult.recommendations
        }
      };

      // Save to workflow state (this automatically marks Stage 1 as completed)
      dispatch(setWaterProfile(workflowWaterProfile));

      // Navigate to next stage
      navigate('/formulation');

    } catch (error) {
      console.error('Error confirming water profile:', error);
      setError(`Failed to confirm water profile: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setConfirming(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return 'success';
      case 'acceptable': return 'info';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'optimal': return <CheckIcon />;
      case 'acceptable': return <InfoIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading water profiles...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  if (waterProfiles.length === 0) {
    return (
      <Alert severity="info">
        No water profiles found in database. Create a water profile first to analyze it.
      </Alert>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Water Profile Analysis
          </Typography>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Water Profile to Analyze</InputLabel>
            <Select
              value={selectedProfileId}
              onChange={(e) => handleProfileChange(e.target.value)}
              label="Select Water Profile to Analyze"
            >
              {waterProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name} (pH: {profile.ph}, EC: {profile.ec} mS/cm)
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {analyzing && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Analyzing water profile...
              </Typography>
              <LinearProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {analysisResult && (
        <Grid container spacing={3}>
          {/* Overall Score */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Overall Quality Score
                </Typography>
                <Typography variant="h2" color="primary">
                  {analysisResult.overallScore}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  out of 100
                </Typography>
                <Chip
                  label={analysisResult.compatibility.toUpperCase()}
                  color={getStatusColor(analysisResult.compatibility) as any}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Parameter Status */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Parameter Analysis
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid size={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(analysisResult.phStatus)}
                      <Typography variant="body2">
                        pH: {waterProfiles.find(p => p.id === selectedProfileId)?.ph}
                      </Typography>
                      <Chip
                        label={analysisResult.phStatus}
                        color={getStatusColor(analysisResult.phStatus) as any}
                        size="small"
                      />
                    </Box>
                  </Grid>

                  <Grid size={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(analysisResult.ecStatus)}
                      <Typography variant="body2">
                        EC: {waterProfiles.find(p => p.id === selectedProfileId)?.ec} mS/cm
                      </Typography>
                      <Chip
                        label={analysisResult.ecStatus}
                        color={getStatusColor(analysisResult.ecStatus) as any}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Element Analysis:
                </Typography>
                
                <Grid container spacing={1}>
                  {analysisResult.elementAnalysis.map((element, index) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {getStatusIcon(element.status)}
                        <Typography variant="caption">
                          {element.symbol}: {element.concentration}
                        </Typography>
                        <Chip
                          label={element.status}
                          color={getStatusColor(element.status) as any}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Recommendations */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ScienceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Recommendations
                </Typography>

                <List>
                  {analysisResult.recommendations.map((recommendation, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <InfoIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText primary={recommendation} />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Workflow Confirmation */}
          <Grid size={12}>
            <Card sx={{
              border: '2px solid',
              borderColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                ? 'success.main'
                : 'warning.main',
              backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                ? 'success.50'
                : 'warning.50'
            }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <VerifiedIcon
                    color={analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                      ? 'success'
                      : 'warning'
                    }
                    sx={{ fontSize: 32 }}
                  />
                  <Box>
                    <Typography variant="h6">
                      Water Profile Analysis Complete
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {waterProfiles.find(p => p.id === selectedProfileId)?.name} -
                      Quality Score: {analysisResult.overallScore}/100 ({analysisResult.compatibility.toUpperCase()})
                    </Typography>
                  </Box>
                </Box>

                <Alert
                  severity={analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                    ? 'success'
                    : 'warning'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    {analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                      ? '✅ This water profile is suitable for hydroponic cultivation and will be used for nutrient calculations.'
                      : '⚠️ This water profile has some limitations but can still be used. Please review the recommendations above.'
                    }
                  </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Click "Confirm & Continue" to save this water profile to your workflow and proceed to crop formulation selection.
                </Typography>
              </CardContent>

              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSelectedProfileId('');
                    setAnalysisResult(null);
                  }}
                  disabled={confirming}
                >
                  Select Different Profile
                </Button>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleConfirmWaterProfile}
                  disabled={confirming || !selectedProfileId || !analysisResult}
                  endIcon={confirming ? <CircularProgress size={20} /> : <ArrowForwardIcon />}
                  sx={{
                    minWidth: 200,
                    backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                      ? 'success.main'
                      : 'warning.main',
                    '&:hover': {
                      backgroundColor: analysisResult.compatibility === 'excellent' || analysisResult.compatibility === 'good'
                        ? 'success.dark'
                        : 'warning.dark'
                    }
                  }}
                >
                  {confirming ? 'Confirming...' : 'Confirm & Continue to Formulation'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default WaterProfileAnalysis;
